import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { NotificationItem } from '@/types'

import Link from 'next/link'
import { useCallback, useRef, useState } from 'react'

import {
    AtSign,
    Bell,
    BellOff,
    Bookmark,
    ChevronDown,
    CircleCheck,
    CircleHelp,
    FilePlus2,
    FolderOpen,
    MessageSquare,
    RotateCcw,
    Sparkles,
    UserRound
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { toFaDigits } from '@/lib/format'
import { useDismiss } from '@/hooks/use-dismiss'
import { useNotifications } from '@/components/generals/providers'
import { Avatar } from '@/components/ds/avatar'
import { ToggleGroup } from '@/components/ds/toggle-group'
import { EmptyState } from '@/components/ds/feedback'
import { CURRENT_USER } from '@/constants/people'
import { UNIT_BY_KEY } from '@/constants/taxonomy'

const KIND_ICONS: Record<NotificationItem['kind'], { icon: LucideIcon, className: string }> = {
    approved: { icon: CircleCheck, className: 'bg-success-soft text-success-strong' },
    comment: { icon: MessageSquare, className: 'bg-primary-soft text-primary-deep' },
    returned: { icon: RotateCcw, className: 'bg-destructive-soft text-destructive' },
    new: { icon: Sparkles, className: 'bg-secondary text-navy' },
    mention: { icon: AtSign, className: 'bg-primary-soft text-primary-deep' }
}

const triggerClass = 'relative flex cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 aria-expanded:bg-secondary aria-expanded:text-navy'

const panelClass = 'absolute end-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-border bg-popover shadow-pop animate-in fade-in slide-in-from-top-1 duration-150'

export const NotificationsMenu: FC = (): ReactNode => {
    const { items, unreadCount, markRead, markAllRead } = useNotifications()
    const [open, setOpen] = useState(false)
    const [filter, setFilter] = useState<'all' | 'unread'>('all')
    const ref = useRef<HTMLDivElement>(null)
    const close = useCallback(() => setOpen(false), [])

    useDismiss(ref, open, close)

    const visible = filter === 'unread' ? items.filter(item => item.unread) : items

    return (
        <div ref={ref} className='relative'>
            <button
                type='button'
                onClick={() => setOpen(value => !value)}
                aria-expanded={open}
                aria-haspopup='dialog'
                aria-label={unreadCount ? `اعلان‌ها، ${toFaDigits(unreadCount)} مورد خوانده‌نشده` : 'اعلان‌ها'}
                className={cn(triggerClass, 'size-10')}
            >
                <Bell className='size-5' aria-hidden='true' />

                {unreadCount > 0 && (
                    <span className='absolute end-1.5 top-1.5 flex min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-4 text-white ring-2 ring-card'>
                        {toFaDigits(unreadCount)}
                    </span>
                )}
            </button>

            {open && (
                <div role='dialog' aria-label='اعلان‌ها' className={cn(panelClass, 'w-[min(24rem,calc(100vw-1.5rem))] -me-12 sm:me-0')}>
                    <div className='flex items-center justify-between gap-3 border-b border-border px-4 py-3'>
                        <p className='text-sm font-bold text-navy'>اعلان‌ها</p>

                        <button
                            type='button'
                            onClick={markAllRead}
                            disabled={unreadCount === 0}
                            className='cursor-pointer text-small font-semibold text-primary-strong transition-colors hover:text-primary-deep disabled:cursor-default disabled:text-subtle'
                        >
                            علامت‌گذاری همه به‌عنوان خوانده‌شده
                        </button>
                    </div>

                    <div className='px-4 pt-3'>
                        <ToggleGroup
                            label='فیلتر اعلان‌ها'
                            value={filter}
                            onChange={setFilter}
                            options={[
                                { value: 'all', label: 'همه', count: items.length },
                                { value: 'unread', label: 'خوانده‌نشده', count: unreadCount }
                            ]}
                        />
                    </div>

                    {visible.length === 0 ? (
                        <EmptyState compact icon={BellOff} title='همه اعلان‌ها را خوانده‌اید' description='اعلان‌های جدید درباره دانش‌های شما اینجا نمایش داده می‌شوند.' />
                    ) : (
                        <ul className='flex max-h-[60vh] flex-col overflow-y-auto p-2'>
                            {visible.map(item => {
                                const kind = KIND_ICONS[item.kind]
                                const Icon = kind.icon

                                return (
                                    <li key={item.id}>
                                        <Link
                                            href={item.href}
                                            onClick={() => {
                                                markRead(item.id)
                                                close()
                                            }}
                                            className='flex gap-3 rounded-lg px-2.5 py-2.5 transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/45'
                                        >
                                            <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-full', kind.className)}>
                                                <Icon className='size-4' aria-hidden='true' />
                                            </span>

                                            <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                                                <span className={cn('text-small', item.unread ? 'font-semibold text-navy' : 'text-foreground')}>
                                                    {item.text}
                                                </span>

                                                <span className='line-clamp-1 text-small text-primary-deep'>
                                                    {item.target}
                                                </span>

                                                <span className='text-caption text-muted-foreground'>
                                                    {item.time}
                                                </span>
                                            </span>

                                            {item.unread && (
                                                <span className='mt-2 size-2 shrink-0 rounded-full bg-primary' aria-label='خوانده‌نشده' />
                                            )}
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    )
}

const PROFILE_LINKS = [
    { href: `/people/${CURRENT_USER.slug}`, label: 'پروفایل دانشی من', icon: UserRound },
    { href: '/me', label: 'دانش‌های من', icon: FolderOpen },
    { href: '/saved', label: 'علاقه‌مندی‌ها', icon: Bookmark },
    { href: '/new', label: 'ثبت دانش جدید', icon: FilePlus2 },
    { href: '/faq', label: 'راهنمای استفاده', icon: CircleHelp }
]

export const ProfileMenu: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const close = useCallback(() => setOpen(false), [])

    useDismiss(ref, open, close)

    return (
        <div ref={ref} className='relative'>
            <button
                type='button'
                onClick={() => setOpen(value => !value)}
                aria-expanded={open}
                aria-haspopup='menu'
                aria-label='منوی کاربر'
                className={cn(triggerClass, 'h-10 gap-2 ps-1 pe-1 md:pe-2.5')}
            >
                <Avatar name={CURRENT_USER.name} tone={CURRENT_USER.tone} size='sm' />

                <span className='hidden flex-col items-start leading-tight md:flex'>
                    <span className='text-sm font-semibold text-navy'>{CURRENT_USER.name}</span>
                    <span className='text-caption text-muted-foreground'>{UNIT_BY_KEY[CURRENT_USER.unit]?.title}</span>
                </span>

                <ChevronDown className='hidden size-4 md:block' aria-hidden='true' />
            </button>

            {open && (
                <div role='menu' aria-label='منوی کاربر' className={cn(panelClass, 'w-64')}>
                    <div className='flex items-center gap-3 border-b border-border p-4'>
                        <Avatar name={CURRENT_USER.name} tone={CURRENT_USER.tone} size='md' />

                        <div className='flex min-w-0 flex-col'>
                            <span className='truncate text-sm font-bold text-navy'>{CURRENT_USER.name}</span>
                            <span className='truncate text-caption text-muted-foreground'>{CURRENT_USER.role}</span>
                        </div>
                    </div>

                    <ul className='flex flex-col p-2'>
                        {PROFILE_LINKS.map(link => {
                            const Icon = link.icon

                            return (
                                <li key={link.href} role='none'>
                                    <Link
                                        href={link.href}
                                        role='menuitem'
                                        onClick={close}
                                        className='flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-secondary hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/45'
                                    >
                                        <Icon className='size-4 text-muted-foreground' aria-hidden='true' />
                                        {link.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            )}
        </div>
    )
}
